import React, { useState, useRef, useEffect } from "react";

export default function CustomSelect({
  label = null,
  options,
  defaultIndex = null,
  onChange,
  className = "",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(defaultIndex || 0);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  const selectedOption = options && options[selectedIndex] ? options[selectedIndex] : null;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Sync highlighted index with selected index when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(selectedIndex);
    }
  }, [isOpen, selectedIndex]);

  // Handle Keyboard Navigation
  const handleKeyDown = (e) => {
    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        if (isOpen) {
          setSelectedIndex(highlightedIndex);
          setIsOpen(false);
          buttonRef.current?.focus();
        } else {
          setIsOpen(true);
        }
        break;
      case "ArrowDown":
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((prev) =>
            prev < options.length - 1 ? prev + 1 : prev,
          );
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        buttonRef.current?.focus();
        break;
      case "Tab":
        // Let Tab key naturally close and move focus away
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleOptionClick = (index) => {
    setSelectedIndex(index);
    setIsOpen(false);
    buttonRef.current?.focus();
    onChange && onChange(index);
  };

  return (
    <div ref={containerRef} className={`relative w-full sm:w-64 text-lg" ${className}`}>
      {label && (
        <label
          id="custom-select-label"
          className="text-sm text-black font-semibold"
        >
          {label}
        </label>
      )}

      <button
        ref={buttonRef}
        type="button"
        id="custom-select-button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls="custom-select-listbox"
        aria-labelledby="custom-select-label custom-select-button"
        aria-activedescendant={
          isOpen ? `option-${highlightedIndex}` : undefined
        }
        onKeyDown={handleKeyDown}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full h-14 px-4  text-black focus:outline-none focus:ring-2 focus:ring-jay-blue focus:border-jay-blue transition-all text-left"
      >
        <div>{selectedOption?.label}</div>

        <svg
          className={`w-5 h-5 text-black transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true" 
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* The Dropdown Options Menu */}
      {isOpen && (
        <ul
          id="custom-select-listbox"
          role="listbox"
          aria-labelledby="custom-select-label"
          tabIndex={-1}
          className="absolute z-10 w-full mt-1 max-h-60 overflow-auto bg-white border border-slate-200 shadow-lg focus:outline-none py-1"
        >
          {options && options.map((option, index) => {
            const isSelected = index === selectedIndex;
            const isHighlighted = index === highlightedIndex;

            return (
              <li
                key={`option-${index}`}
                role="option"
                aria-selected={isSelected}
                onMouseEnter={() => setHighlightedIndex(index)}
                onClick={() => handleOptionClick(index)}
                className={`flex items-center justify-between px-4 py-3 cursor-pointer select-none transition-colors
                  ${isHighlighted ? "bg-indigo-50 text-indigo-900" : "text-slate-900"}
                  ${isSelected ? "font-semibold" : "font-normal"}
                `}
              >
                <span>{option.label}</span>

                {/* Optional Checkmark for Selected State */}
                {isSelected && (
                  <svg
                    className="w-5 h-5 text-jay-blue"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
